// =============================================================================
// VUE DOCUMENT DEVIS (façon Pennylane) — embarque le PDF ReportLab existant
// =============================================================================
// Ouvrir un devis affiche le DOCUMENT (PDF rendu via l'endpoint preview base64),
// pas le wizard. "Modifier" rouvre le wizard (brouillon → modifiable). Mécanisme
// partagé (OMV/climat) ; branding par tenant déjà géré côté générateur PDF.
// 0 backend : réutilise POST /api/metier/documents/devis/{id}/pdf/preview.
import { useState, useEffect } from "react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { isInstallateurProfile } from "../../utils/tenantProfile";
import { withDevisPrefix, devisPrefixEnabled } from "../../utils/devisDisplay";
import { devisStatutLabel, estModifiableDevis } from "../../utils/devisStatut";
import { useConfigList } from "../../hooks/useConfigList";
import DevisStatutOmvSelect from "../../components/omv/DevisStatutOmvSelect";
import { Badge } from "../../components/ui/badge";
import { ArrowLeft, Pencil, Download, RefreshCw, FileText, Send } from "lucide-react";
import SendDevisOmvModal from "../../components/omv/SendDevisOmvModal";
import DevisEnvoiTrace from "../../components/omv/DevisEnvoiTrace";

const API = process.env.REACT_APP_BACKEND_URL;

const STATUT_LABELS = {
  draft: "Brouillon", brouillon: "Brouillon", validation: "En validation",
  locked: "Verrouillé", emis: "Émis", envoye_signature: "Envoyé (signature)",
  devis_signe: "Signé", signe: "Signé", dossier_depose: "Déposé", paye: "Payé",
  archive: "Archivé", refuse: "Refusé", annule: "Annulé",
};

export default function DevisDocument({ dossierId, devisId, tenantId, onBack, onModifier, onNouveauDevis }) {
  const [loading, setLoading] = useState(true);
  const [pdfB64, setPdfB64] = useState(null);
  const [devis, setDevis] = useState(null);
  const [error, setError] = useState(null);
  const [numeroDraft, setNumeroDraft] = useState("");
  const [savingNumero, setSavingNumero] = useState(false);
  const [dateDraft, setDateDraft] = useState("");
  const [savingDate, setSavingDate] = useState(false);
  // Incrémenté après une sauvegarde (numéro/date) → re-déclenche le fetch de
  // l'aperçu PDF pour refléter la modification.
  const [reloadTick, setReloadTick] = useState(0);

  // Profil installateur (OMV, Climat) : numéro de devis manuel éditable en brouillon.
  const isOmv = isInstallateurProfile();
  const { items: statutsDevis } = useConfigList("statuts_devis");
  const [sendOpen, setSendOpen] = useState(false);

  const buildHeaders = (json) => {
    const t = localStorage.getItem("session_token");
    return {
      ...(json ? { "Content-Type": "application/json" } : {}),
      "X-Tenant-ID": tenantId || localStorage.getItem("current_tenant_slug") || "",
      ...(t ? { Authorization: `Bearer ${t}` } : {}),
    };
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const [pdfRes, devisRes] = await Promise.all([
          fetch(`${API}/api/metier/documents/devis/${devisId}/pdf/preview`,
            { method: "POST", headers: buildHeaders(true), credentials: "include" }),
          fetch(`${API}/api/metier/devis/${devisId}`,
            { headers: buildHeaders(), credentials: "include" }),
        ]);
        if (pdfRes.ok) {
          const d = await pdfRes.json();
          if (alive) setPdfB64(d.pdf_base64 || null);
        } else if (alive) {
          setError("Impossible de générer l'aperçu PDF du devis.");
        }
        if (devisRes.ok) {
          const dv = await devisRes.json();
          if (alive) setDevis(dv.devis || dv);
        }
      } catch {
        if (alive) setError("Erreur réseau pendant le chargement du document.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [devisId, tenantId, reloadTick]);

  const statut = devis?.statut;
  const numero = devis?.numero || devis?.numero_provisoire || String(devisId || "").slice(0, 8);

  // Synchronise les champs éditables avec le devis chargé.
  useEffect(() => { setNumeroDraft(devis?.numero || ""); }, [devis?.numero]);
  useEffect(() => { setDateDraft(devis?.date_devis || ""); }, [devis?.date_devis]);

  // Enregistre le numéro manuel (OMV, brouillon) via PUT /devis/{id}.
  const saveNumero = async () => {
    const val = (numeroDraft || "").trim();
    if (val === (devis?.numero || "")) return;  // pas de changement
    setSavingNumero(true);
    try {
      const res = await fetch(`${API}/api/metier/devis/${devisId}`, {
        method: "PUT", headers: buildHeaders(true), credentials: "include",
        body: JSON.stringify({ numero: val }),
      });
      if (res.ok) {
        const d = await res.json();
        setDevis(d.devis || d);
        setReloadTick(t => t + 1);  // rafraîchit l'aperçu PDF
      }
    } finally {
      setSavingNumero(false);
    }
  };

  // Enregistre la date du devis ÉDITABLE (OMV, brouillon) via PUT /devis/{id}.
  // Même mécanisme que le numéro ; source unique date_devis (cohérent wizard).
  const saveDate = async () => {
    const val = (dateDraft || "").trim();
    if (val === (devis?.date_devis || "")) return;  // pas de changement
    setSavingDate(true);
    try {
      const res = await fetch(`${API}/api/metier/devis/${devisId}`, {
        method: "PUT", headers: buildHeaders(true), credentials: "include",
        body: JSON.stringify({ date_devis: val }),
      });
      if (res.ok) {
        const d = await res.json();
        setDevis(d.devis || d);
        setReloadTick(t => t + 1);  // rafraîchit l'aperçu PDF (date sur le devis)
      }
    } finally {
      setSavingDate(false);
    }
  };

  const telecharger = () => {
    if (!pdfB64) return;
    const a = document.createElement("a");
    a.href = `data:application/pdf;base64,${pdfB64}`;
    a.download = `Devis_${String(numero).replace(/\//g, "-")}.pdf`;
    a.click();
  };

  return (
    <div className="max-w-5xl mx-auto space-y-4" data-testid="devis-document">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={onBack} aria-label="Retour" className="text-[var(--oh-text-secondary)]">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <FileText className="w-5 h-5 text-[var(--oh-info-text)]" />
          <h1 className="text-lg font-medium text-[var(--oh-text)]">{devisPrefixEnabled() ? withDevisPrefix(numero) : `Devis ${numero}`}</h1>
          {statut && (
            <Badge className="text-xs px-2 py-0.5"
              style={{
                background: "var(--oh-stat-blue-bg, rgba(59,130,246,0.15))",
                color: "var(--oh-stat-blue-text, #93c5fd)",
                border: "1px solid var(--oh-stat-blue-border, rgba(59,130,246,0.3))",
              }}
              data-testid="devis-statut-badge">
              {devisStatutLabel(devis, statutsDevis, STATUT_LABELS[statut] || statut)}
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          {/* OMV : numéro de devis manuel éditable en brouillon (PUT /devis/{id}).
              Obligatoire avant verrouillage (le backend bloque le LOCK sans numéro). */}
          {isOmv && estModifiableDevis(statut) && (
            <div className="flex items-center gap-1">
              <span className="text-xs text-[var(--oh-text-muted)]">N° devis</span>
              {/* Préfixe « Devis - » VISUEL (addon) — JJ ne colle QUE le numéro du
                  Sheet ; la valeur enregistrée (numeroDraft) reste NUE. */}
              {devisPrefixEnabled() && (
                <span className="text-xs px-2 h-8 inline-flex items-center rounded-l bg-[var(--oh-bg-elevated)] text-[var(--oh-text-secondary)] border border-r-0 border-white/10"
                  data-testid="devis-numero-prefix">Devis -</span>
              )}
              <Input
                value={numeroDraft}
                onChange={e => setNumeroDraft(e.target.value)}
                onBlur={saveNumero}
                onKeyDown={e => { if (e.key === "Enter") e.currentTarget.blur(); }}
                placeholder="12345 (copier-coller du Sheet)"
                disabled={savingNumero}
                className={`h-8 w-44 bg-[var(--oh-bg-elevated)] border-white/10 text-[var(--oh-text)] text-sm${devisPrefixEnabled() ? " rounded-l-none" : ""}`}
                data-testid="devis-document-numero-omv"
              />
              <span className="text-xs text-[var(--oh-text-muted)] ml-2">Date</span>
              <Input
                type="date"
                value={dateDraft}
                onChange={e => setDateDraft(e.target.value)}
                onBlur={saveDate}
                onKeyDown={e => { if (e.key === "Enter") e.currentTarget.blur(); }}
                disabled={savingDate}
                className="h-8 w-40 bg-[var(--oh-bg-elevated)] border-white/10 text-[var(--oh-text)] text-sm"
                data-testid="devis-document-date-omv"
              />
            </div>
          )}
          <Button variant="outline" size="sm" onClick={telecharger} disabled={!pdfB64}
            className="border-[var(--oh-border)] text-[var(--oh-text-secondary)]" data-testid="devis-document-download">
            <Download className="w-4 h-4 mr-1" /> Télécharger
          </Button>
          {/* OMV : envoi par email depuis l'aperçu (même modal que la fiche/grille). */}
          {isOmv && statut === "emis" && (
            <div className="flex flex-col items-end gap-0.5">
              <Button size="sm" onClick={() => setSendOpen(true)}
                className="bg-[var(--oh-accent-solid)] hover:bg-[var(--oh-accent-solid)]" data-testid="devis-document-send">
                <Send className="w-4 h-4 mr-1" /> {devis?.email_envoi?.status === "sent" ? "Renvoyer" : "Envoyer"}
              </Button>
              <DevisEnvoiTrace envoi={devis?.email_envoi} />
              <DevisStatutOmvSelect
                devis={devis}
                tenantId={tenantId}
                statutsList={statutsDevis}
                onChanged={() => setReloadTick((t) => t + 1)}
              />
            </div>
          )}
          {estModifiableDevis(statut) ? (
            <Button size="sm" onClick={onModifier}
              className="bg-[var(--oh-accent-solid)] text-[var(--oh-accent-contrast)] hover:opacity-90" /* charte-ok: bouton primaire — accent métier */
              data-testid="devis-document-modifier">
              <Pencil className="w-4 h-4 mr-1" /> Modifier
            </Button>
          ) : (
            // Devis SIGNÉ (verrouillé) : on ne modifie pas un signé — on crée un
            // nouveau devis dans le dossier (plusieurs devis/dossier autorisés).
            <div className="flex flex-col items-end gap-0.5">
              <Button size="sm" disabled className="opacity-60"
                title="Devis signé — créez un nouveau devis pour le modifier"
                data-testid="devis-document-modifier-locked">
                <Pencil className="w-4 h-4 mr-1" /> Modifier
              </Button>
              {onNouveauDevis && (
                <Button size="sm" onClick={onNouveauDevis}
                  className="bg-[var(--oh-accent-solid)] hover:bg-[var(--oh-accent-solid)]"
                  data-testid="devis-document-nouveau-devis">
                  <FileText className="w-4 h-4 mr-1" /> Créer un nouveau devis
                </Button>
              )}
            </div>
          )}
        </div>
      </div>

      {loading ? (
        <div className="text-center py-20 text-[var(--oh-text-secondary)]">
          <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-3" /> Génération de l'aperçu…
        </div>
      ) : error ? (
        <div className="p-4 bg-[var(--oh-error-bg)] border border-[var(--oh-error-border)] rounded-xl text-[var(--oh-error-text)] text-sm">{error}</div>
      ) : pdfB64 ? (
        <iframe
          title={`Devis ${numero}`}
          src={`data:application/pdf;base64,${pdfB64}`}
          className="w-full rounded-xl border border-[var(--oh-border)]"
          style={{ height: "80vh", background: "white" }}
          data-testid="devis-document-iframe"
        />
      ) : (
        <div className="p-4 bg-[var(--oh-bg-elevated)] border border-[var(--oh-border)] rounded-xl text-[var(--oh-text-secondary)] text-sm">
          Aperçu indisponible.
        </div>
      )}

      <SendDevisOmvModal
        open={sendOpen}
        onClose={() => setSendOpen(false)}
        devis={devis}
        tenantId={tenantId}
        onSent={() => setReloadTick((t) => t + 1)}
      />
    </div>
  );
}
